<template>
	<div class="Table-container">
		<table class="Table" :class="classes">
			<caption>
				{{
					computedCaption
				}}
			</caption>

			<!-- Table header -->
			<thead>
				<tr :class="minorHeadings ? 'Table-row--minorHeading' : ''" ref="tableHeader">
					<!-- First column header for rowNumbers or rank. -->
					<th v-if="rowNumbers || showRank" scope="col">
						<span v-if="rowNumbers" class="is-visuallyHidden">{{ translations.index }}</span>
						<span v-if="showRank" class="is-visuallyHidden">{{ translations.rank }}</span>
					</th>

					<!-- <th> from columns array prop -->
					<template v-for="(column, columnIndex) in columns">
						<th
							:key="columnIndex"
							v-if="!column.hidden"
							:class="columnClasses(columnIndex)"
							scope="col"
							:aria-sort="ariaSort(columnIndex)"
							:style="columnWidth(columnIndex)"
						>
							<!-- optional icon -->
							<CloIcon v-if="column.icon" :symbol="column.icon" />

							<!-- buttons to set sort column -->
							<a
								v-if="column.sortable"
								role="button"
								tabindex="0"
								class="u-text-2"
								@click="sortBy(columnIndex)"
								@keyup.enter="sortBy(columnIndex)"
								@keyup.space="sortBy(columnIndex)"
							>
								<span>{{ column.label }}</span>
								<CloIcon symbol="triangleDown" size="sm" :style="sortIconStyle(column)" />
							</a>

							<!-- no sort, plain text -->
							<span v-else class="u-text-2" :class="{ 'is-visuallyHidden': column.srOnly }">{{ column.label }}</span>
						</th>
					</template>
				</tr>
			</thead>

			<!-- the table body -->
			<tbody is="transition-group" :name="animateSort ? 'animate-rows' : ''">
				<!-- iterate over each row of data -->
				<tr
					v-for="(row, rowIndex) in sortedData"
					:key="row[0].key"
					:class="expanded || rowIndex < rowLimit ? '' : 'Table-row--sm-visuallyHidden'"
				>
					<!-- rowNumbers -->
					<th v-if="rowNumbers" :key="'row' + rowIndex" scope="row" class="Table-cell--index">{{ rowIndex + 1 }}</th>

					<!-- rank -->
					<th v-if="showRank" :key="'row' + rowIndex" scope="row" class="Table-cell--index">{{ row[0].rank }}</th>

					<!-- the table cell -->
					<template v-for="(cell, cellIndex) in row">
						<template v-if="!columns[cellIndex].hidden">
							<!-- row headers -->
							<th
								v-if="cell.header"
								:key="'tableCell-' + rowIndex + '-' + cellIndex"
								:class="cellClasses(cell, cellIndex)"
								scope="row"
							>
								{{ cell.value }}
							</th>

							<!-- cell with stat bar -->
							<td v-else-if="columns[cellIndex].statBar" :key="'statBar' + cellIndex">
								<CloStatBar
									:percentage="(cell.value * 100) / columns[cellIndex].max"
									:value="cell.value.toString()"
									:color="sortIndex === cellIndex ? 'secondary' : ''"
								/>
							</td>

							<!-- normal cell -->
							<td v-else :key="cellIndex" :class="cellClasses(cell, cellIndex)">
								<!-- optional loading state for each cell -->
								<CloLoadingAnimation v-if="cell.loading" class="LoadingAnimation--small" />

								<!-- optional link inside cell -->
								<a v-else-if="cell.href" :href="cell.href" @click="$emit('table-link-click', $event)">
									<template v-if="cell.speciesName">
										<span class="Table-cell-speciesName">
											<span class="Table-cell-speciesName--comName">{{ cell.speciesName.comName }}</span>
											<span class="Table-cell-speciesName--sciName">{{ cell.speciesName.sciName }}</span>
										</span>
									</template>
									<template v-else>
										{{ cell.value }}
									</template>
								</a>

								<!-- optional method call from cell -->
								<button
									v-else-if="cell.method"
									type="button"
									@click="$emit('table-method-call', cell.method, cell.data)"
									class="Table-cell--methodCall"
								>
									<template v-if="cell.speciesName">
										<span class="Table-cell-speciesName">
											<span class="Table-cell-speciesName--comName">{{ cell.speciesName.comName }}</span>
											<span class="Table-cell-speciesName--sciName">{{ cell.speciesName.sciName }}</span>
										</span>
									</template>
									<template v-else>
										{{ cell.value }}
									</template>
								</button>

								<!-- no link or call -->
								<span v-else>
									<template v-if="cell.speciesName">
										<span class="Table-cell-speciesName">
											<span class="Table-cell-speciesName--comName">{{ cell.speciesName.comName }}</span>
											<span class="Table-cell-speciesName--sciName">{{ cell.speciesName.sciName }}</span>
										</span>
									</template>
									<template v-else>
										{{ cell.value }}
									</template>
								</span>
							</td>
						</template>
					</template>
				</tr>
			</tbody>
		</table>

		<!-- optional show more/less buttons -->
		<button
			v-if="rowLimit > 0 && !expanded && sortedData.length > rowLimit"
			type="button"
			aria-hidden="true"
			tabindex="-1"
			class="Button Button--small Button--hollow u-hideForMedium"
			@click="expanded = true"
		>
			{{ translations.showMore }} ({{ sortedData.length }})
		</button>
		<button
			v-else-if="rowLimit > 0 && expanded && sortedData.length > rowLimit"
			type="button"
			aria-hidden="true"
			tabindex="-1"
			class="Button Button--small Button--hollow u-hideForMedium"
			@click="expanded = false"
		>
			{{ translations.showLess }}
		</button>
	</div>
</template>

<script>
import CloButton from '@/base/CloButton/CloButton.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';
import CloStatBar from '@/visualizations/CloStatBar/CloStatBar.vue';
export default {
	components: { CloButton, CloIcon, CloLoadingAnimation, CloStatBar },
	props: {
		/**
		 * Caption for the table
		 *
		 * */
		caption: {
			type: String,
			required: true,
		},
		/**
		 * To use row numbers or not
		 *
		 */
		rowNumbers: {
			type: Boolean,
			default: false,
		},
		/**
		 * Primary colored row backgrounds
		 *
		 */
		primary: {
			type: Boolean,
			default: false,
		},
		/**
		 * The table columns
		 *
		 */
		columns: {
			type: Array,
			default() {
				return [];
			},
			required: true,
		},
		/**
		 * The data as 2 dimensional array
		 *
		 */
		tableData: {
			type: Array,
			default() {
				return [];
			},
			required: true,
		},
		/**
		 * Reverse colors
		 *
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * Lighter text
		 *
		 */
		muted: {
			type: Boolean,
			default: false,
		},
		/**
		 * Only a border under the header row
		 *
		 */
		headerBorder: {
			type: Boolean,
			default: false,
		},
		/**
		 * Headings use "minor" typography style (uppercase)
		 *
		 */
		minorHeadings: {
			type: Boolean,
			default: false,
		},
		/**
		 * Table size: can be sm or lg
		 *
		 */
		size: {
			type: String,
			default: '',
		},
		/**
		 * Removes all borders
		 *
		 */
		noBorder: {
			type: Boolean,
			default: false,
		},
		/**
		 * No borders around header row. TODO: deprecate
		 *
		 */
		borderlessHeaders: {
			type: Boolean,
			default: false,
		},
		/**
		 * Column headers not bold. TODO: deprecate, use minorHeadings instead
		 *
		 */
		mutedHeaders: {
			type: Boolean,
			default: false,
		},
		/**
		 * No alternating row colors
		 *
		 */
		clearRows: {
			type: Boolean,
			default: false,
		},
		/**
		 * horizontal rows between lines
		 *
		 */
		rowLines: {
			type: Boolean,
			default: false,
		},
		/**
		 * The column index to initially sort by (first column is 0)
		 *
		 */
		sortColumn: {
			type: Number,
			default: 0,
		},
		/**
		 * Only show a certain number of rows, adds button to show more
		 *
		 */
		rowLimit: {
			type: Number,
			default: 0,
		},
		/**
		 * Special large table design for displaying eBird species stats
		 *
		 */
		speciesStats: {
			type: Boolean,
			default: false,
		},
		/**
		 * Animate row sorting
		 *
		 */
		animateSort: {
			type: Boolean,
			default: false,
		},
		/**
		 * Allow sorting in both ascending and descending
		 *
		 */
		sortBothDirections: {
			type: Boolean,
			default: false,
		},
		/**
		 * Show rank in first column, calculated according to current sort, and handling ties (for leaderboards)
		 *
		 */
		showRank: {
			type: Boolean,
			default: false,
		},
		/**
		 * Sticky header
		 *
		 */
		stickyHeader: {
			type: Boolean,
			default: false,
		},
		/**
		 * Translations to override default English strings: index, rank, showMore, showLess, sortedByDescending, sortedByAscending
		 *
		 */
		messages: {
			type: Object,
			default() {
				return {};
			},
		},
	},
	data() {
		return {
			expanded: true,
			sortIndex: 0,
			tableDataWithKey: this.tableData,
			sortedData: [],
			computedCaption: '',
			scrollTimer: false,
			translations: {},
			defaultMessages: {
				index: 'Index',
				rank: 'Rank',
				showMore: 'Show more',
				showLess: 'Show less',
				sortedByDescending: 'Sorted by {0} descending',
				sortedByAscending: 'Sorted by {0} ascending',
			},
		};
	},
	beforeMount() {
		// merge messages prop with defaultMessages into translations
		Object.assign(this.translations, this.defaultMessages, this.messages);
	},
	mounted() {
		this.sortIndex = this.sortColumn;

		this.expanded = this.rowLimit === 0;

		this.computedCaption = this.caption;

		this.columns.forEach((column, columnIndex) => {
			if (column.statBar) {
				column.max = 0;
				this.tableData.forEach((row) => {
					if (row[columnIndex].value > column.max) {
						column.max = row[columnIndex].value;
					}
				});
			}
			if (column.date) {
				this.tableData.forEach((row) => {
					if (row[columnIndex].hasOwnProperty('sortValue')) {
						if (row[columnIndex].sortValue === null) {
							row[columnIndex].sortValue = '0';
							row[columnIndex].value = '';
						} else {
							let date = new Date(row[columnIndex].sortValue);
							row[columnIndex].value = date.toLocaleDateString(window.navigator.locale, {
								month: 'short',
								day: 'numeric',
								year: 'numeric',
							});
						}
					}
				});
			}
		});
		// set keys for rows (needed for sort animation)
		this.tableDataWithKey.forEach((row, index) => {
			row[0].key = index;
		});
		if (this.sortable) {
			// initial sort
			this.sortBy(this.sortIndex, true);
		} else {
			// don't sort anything
			this.sortedData = this.tableDataWithKey;
			if (this.showRank) {
				this.calculateRank();
			}
		}

		if (this.stickyHeader) {
			document.addEventListener('scroll', this.setStickyHeader);
		}
	},
	beforeDestroy() {
		if (this.stickyHeader) {
			document.removeEventListener('scroll', this.setStickyHeader);
		}
	},
	methods: {
		sortBy(columnIndex, initialSort) {
			// make a copy
			this.sortedData = this.tableDataWithKey.slice(0);

			// an array of columns to use for tiebreakers, default to [] if not set
			let multiSort = this.columns[columnIndex].hasOwnProperty('multiSort') ? this.columns[columnIndex].multiSort.slice(0) : [];

			// the following logic is a bit maddening!
			// each column has a default sort order
			// it can only be toggled if it's not the initial sorting (on component mount)
			// and if sortBothDirections is allowed

			// temporarily store default sort order
			let order = this.columns[columnIndex].sort;
			// if switching current sorted column direction
			if (this.sortBothDirections && this.sortIndex === columnIndex && !initialSort) {
				// toggle the order
				order = this.columns[columnIndex].currentSortOrder === 'desc' ? 'asc' : 'desc';
			}
			// after maybe toggling, set current sort order
			this.columns[columnIndex].currentSortOrder = order;

			// the sort function
			this.sortedData.sort((a, b) => {
				return this.compare(a, b, columnIndex, order, multiSort);
			});

			// set current sort index
			this.sortIndex = columnIndex;

			// if showRank, calculate rank
			if (this.showRank) {
				this.calculateRank();
			}

			this.setCaption();
		},
		// i = current index to sort
		// o = order
		// m = array of fallback indexes
		compare(a, b, i, o, m) {
			const first = a[i].hasOwnProperty('sortValue') ? a[i].sortValue : a[i].value;
			const second = b[i].hasOwnProperty('sortValue') ? b[i].sortValue : b[i].value;

			if (first > second) {
				return o === 'desc' ? -1 : 1;
			}
			if (first < second) {
				return o === 'desc' ? 1 : -1;
			}
			// multiSort tiebreaker recursion!
			if (m.length) {
				return this.compare(a, b, m[0], this.columns[m[0]].sort, m.slice(1));
			}
			return 0;
		},
		ariaSort(index) {
			if (this.sortable && this.sortIndex === index) {
				if (this.columns[index].currentSortOrder == 'desc') {
					return 'descending';
				} else {
					return 'ascending';
				}
			}
		},
		sortIconStyle(column) {
			const asc = 'transform:rotate(180deg)';
			if (column.currentSortOrder === 'asc') {
				return asc;
			}
			if (column.currentSortOrder === 'desc') {
				return '';
			}
			if (column.sort === 'asc') {
				return asc;
			}
			return '';
		},
		calculateRank() {
			// this sets rank (in the first column only for display)
			// but calculates it from the current sort column
			// starts at 1 and handles ties, should work for asc or desc
			let rank = 1;
			this.sortedData.forEach((row, index) => {
				if (index > 0) {
					const sortValue = row[this.sortIndex].hasOwnProperty('sortValue')
						? row[this.sortIndex].sortValue
						: row[this.sortIndex].value;
					const previousSortValue = this.sortedData[index - 1][this.sortIndex].hasOwnProperty('sortValue')
						? this.sortedData[index - 1][this.sortIndex].sortValue
						: this.sortedData[index - 1][this.sortIndex].value;
					if (sortValue !== previousSortValue) {
						rank = index + 1;
					}
				}
				row[0].rank = rank;
			});
			this.$forceUpdate();
		},
		columnClasses(columnIndex) {
			let columnClasses = [];
			let prefix = 'Table-cell--';
			if (this.columns[columnIndex].align) {
				columnClasses.push(prefix + this.columns[columnIndex].align);
			}
			if (this.columns[columnIndex].fit) {
				columnClasses.push(prefix + 'fit');
			}
			if (this.columns[columnIndex].flushLeft) {
				columnClasses.push(prefix + 'flushLeft');
			}
			return columnClasses.join(' ');
		},
		cellClasses(cell, cellIndex) {
			let prefix = 'Table-cell--';
			let classes = [];
			if (cell.highlight) {
				classes.push(prefix + 'highlight');
			}
			return classes.join(' ') + ' ' + this.columnClasses(cellIndex);
		},
		columnWidth(columnIndex) {
			if (this.columns[columnIndex].hasOwnProperty('widthPercentage')) {
				return 'width:' + this.columns[columnIndex].widthPercentage + '%';
			}
		},
		interpolateMessage(message, argument) {
			return message.replace('{0}', argument);
		},
		setCaption() {
			if (this.columns[this.sortIndex].currentSortOrder == 'desc') {
				this.computedCaption =
					this.caption + ' ' + this.interpolateMessage(this.translations.sortedByDescending, this.columns[this.sortIndex].label);
			} else {
				this.computedCaption =
					this.caption + ' ' + this.interpolateMessage(this.translations.sortedByAscending, this.columns[this.sortIndex].label);
			}
		},
		setStickyHeader() {
			this.scrollTimer = setTimeout(() => {
				clearTimeout(this.scrollTimer);
				let position = 0 - this.$el.getBoundingClientRect().top;
				if (position < 0) {
					position = 0;
				}
				this.$refs.tableHeader.style.transform = `translateY(${position}px)`;
			}, 100);
		},
	},
	computed: {
		classes() {
			let classes = [];
			let prefix = 'Table--';
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			if (this.size.length) {
				classes.push(prefix + this.size);
			}
			if (this.noBorder) {
				classes.push(prefix + 'noBorder');
			}
			if (this.primary) {
				classes.push(prefix + 'primary');
			}
			if (this.muted) {
				classes.push(prefix + 'muted');
			}
			if (this.borderlessHeaders) {
				classes.push(prefix + 'borderlessHeaders');
			}
			if (this.headerBorder) {
				classes.push(prefix + 'headerBorder');
			}
			if (this.mutedHeaders) {
				classes.push(prefix + 'mutedHeaders');
			}
			if (this.clearRows) {
				classes.push(prefix + 'clearRows');
			}
			if (this.rowLines) {
				classes.push(prefix + 'rowLines');
			}
			if (this.speciesStats) {
				classes.push(prefix + 'speciesStats');
			}
			if (this.stickyHeader) {
				classes.push(prefix + 'stickyHeader');
			}
			return classes.join(' ');
		},
		sortable() {
			let sortable = false;
			this.columns.forEach((column) => {
				if (column.sortable) {
					sortable = true;
				}
			});
			return sortable;
		},
	},
};
</script>

<style lang="scss">
/* --------------------------------
	Table
-------------------------------- */

/* Main table style */
.Table {
	width: 100%;
	@include stack-md;
	@include text(3);
	background-color: transparent;
	tbody {
		background-color: transparent;
	}
	td,
	th {
		@include inset-sm;
	}
	th {
		font-weight: bold;
		color: $color-text-muted;
		text-align: left;
		[dir='rtl'] & {
			text-align: right;
		}
		// for sortable columns
		a[role='button'] {
			cursor: pointer;
			display: inline-flex;
			align-items: center;
			gap: 0.1em;
			color: $color-text-muted;
			span {
				flex: 0 1 auto;
			}
			.Icon {
				@include icon-sm;
				flex: 1 0 auto;
			}
		}
	}
	thead,
	tfoot,
	tr:nth-of-type(even) {
		background-color: $color-neutral-0;
	}
	thead,
	tfoot,
	tbody {
		border: 1px solid $color-line;
	}
	thead th {
		vertical-align: bottom;
	}
	caption {
		@include visuallyHidden;
	}
	.animate-rows-move {
		transition: transform 0.5s;
	}
	@media (prefers-reduced-motion) {
		.animate-rows-move {
			transition: none;
		}
	}
	a:not(.Button),
	button:not(.Button) {
		text-underline-offset: $space-rel-xs;
		text-decoration-line: underline;
		text-decoration-style: solid;
		text-decoration-thickness: $space-xxs;
		@include interactiveColor(text-decoration-color, transparent, $color-link-hover);
	}
}

.Table-container {
	max-width: 100%;
	overflow: hidden;
	overflow-x: auto;
}
.Table--stickyHeader thead tr {
	background-color: $color-white;
}

// Table row styles -----------------------
.Table-row {
	&:nth-of-type(even) {
		background-color: $color-neutral-0;
	}
}

.Table-row--minorHeading {
	th,
	td,
	.Table-cell {
		color: $color-text-muted;
		font-weight: normal;
		text-transform: uppercase;
		@include inset-squish-sm;
	}
	// for sortable columns, keep gray
	th a[role='button'] {
		color: $color-text-muted;
		display: inline-flex;
		align-items: center;
		.Icon {
			@include icon-sm;
			fill: $color-neutral-3;
		}
	}
}

// Table cell styles -----------------------
// NOTE Table-NAME variants maintained for backwards compatibility
.Table-cell {
	@include inset-sm;
	text-align: left;
	[dir='rtl'] & {
		text-align: right;
	}
}

.Table-cell--minorHeading,
th.Table-cell--minorHeading {
	font-weight: normal;
	text-transform: uppercase;
	color: $color-text-muted;
	@include inset-squish-sm;
}

tr.Table-row--highlight,
td.Table-cell--highlight {
	background-color: $color-primary-0 !important;
}

.Table-cell--center {
	text-align: center;
}

.Table .Table-cell--right,
.Table-cell-number,
.Table-right,
.Table-number {
	text-align: right;
	[dir='rtl'] & {
		text-align: left;
	}
}

.Table-cell--index,
.Table-index {
	color: $color-text-muted;
	width: 1%;
	white-space: nowrap;
}

.Table th.Table-cell--index {
	// row indexes should be <th scope="row"> which are bold by default
	font-weight: normal;
}

.Table-cell--fit {
	width: 1%;
	white-space: nowrap;
}

// species names
.Table-cell-speciesName {
	.Table-cell-speciesName--comName {
		font-weight: bold;
		display: block;
	}
	.Table-cell-speciesName--sciName {
		color: $color-text-muted;
		font-size: 85%;
		font-style: italic;
	}
}

// style the column header green and bold
// shows which column the table is sorted by
th[aria-sort] a[role='button'] {
	color: $color-link-special !important;
	.Icon {
		fill: $color-link-special;
	}
}

.Table-cell--flushLeft,
.Table th.Table-cell--flushLeft {
	padding-left: 0;
	[dir='rtl'] & {
		padding-left: $space-sm;
		padding-right: 0;
	}
}

.Table-cell--methodCall {
	color: $color-link;
}

/* --------------------------------
	Variants
-------------------------------- */
.Table--reverse {
	tbody {
		background-color: $color-black;
	}
	thead,
	tfoot,
	tr:nth-of-type(even) {
		background-color: $color-neutral-5;
	}
	thead,
	tfoot,
	tbody {
		border-color: $color-neutral-5;
	}
	td,
	th,
	.Table-cell {
		color: $color-text-reverse;
		a {
			color: $color-link-reverse;
			&:hover,
			&:focus,
			&:active {
				color: $color-link-hover-reverse;
			}
		}
	}
	th,
	th.Table-cell {
		color: $color-text-reverse;
	}
	.Table-row--highlight,
	.Table-cell--highlight {
		background-color: $color-secondary-5;
	}
	.Table-row--minorHeading,
	.Table-cell--minorHeading {
		color: $color-text-muted;
	}
	thead,
	tbody,
	tfoot {
		border: 0;
		background: transparent;
	}
	tr:nth-of-type(even),
	thead,
	tfoot {
		background-color: transparent;
	}
	th,
	td {
		@include colorReverse;
		border: 1px solid $color-neutral-4;
	}
	a:not(.Button),
	button:not(.Button) {
		@include interactiveColor(text-decoration-color, transparent, $color-link-hover-reverse);
	}
}

/* Primary variant - for using primary highlight color on even rows */
.Table--primary {
	thead,
	tfoot,
	tr:nth-of-type(even) {
		background-color: $color-primary-0;
	}
	thead,
	tfoot,
	tbody {
		border-color: $color-primary-1;
	}
	.Table-row--highlight,
	.Table-cell--highlight {
		background-color: $color-secondary-0;
	}
}

/* No border variant */
.Table--noBorder {
	thead,
	tfoot,
	tbody {
		border: none;
	}
}

/* Clear rows variant - removes bg color from alternating rows */
.Table--clearRows {
	thead {
		background-color: transparent;
	}
	tr:nth-of-type(even) {
		background-color: transparent;
	}
}

/* Horizontal lines between each row */
.Table--rowLines {
	tr {
		background-color: transparent;
		border-bottom: 1px solid $color-neutral-1;
	}
}

/* Small variant - for smaller text and tighter cell padding */
.Table--sm {
	@include text(2);
}

/* Large variant - for larger text and looser cell padding */
.Table--lg {
	@include text(4);
	td,
	th,
	.Table-cell {
		@include inset-md;
	}
}

/* Border only under the header row */
.Table--headerBorder {
	thead {
		background: transparent;
		border: 0;
		border-bottom: 1px solid $color-neutral-1;
	}
	tfoot,
	tbody {
		border: none;
	}
	&.Table--reverse {
		thead {
			background: transparent;
			border: 0;
			border-bottom: 1px solid $color-neutral-4;
		}
		tfoot,
		tbody,
		td,
		th {
			border: none;
		}
	}
}

.Table--muted {
	color: $color-text-muted;
}

// Use-specific variants -------------------------------------------

/* Confirmation variant */
.Table--confirmation {
	line-height: 1;
	tr:nth-of-type(even) {
		background-color: transparent !important;
	}
	td:first-child {
		font-weight: bold;
		width: 33%;
	}
	tbody {
		border-top-width: 15px;
		border-color: transparent;
	}
}

/* Species stats */
.Table--speciesStats {
	margin-bottom: 0;
	width: 100%;
	max-width: 50rem;
	thead,
	tbody,
	tfoot {
		display: block;
		border: 0;
		background: transparent;
	}
	tr:nth-of-type(even),
	thead,
	tfoot {
		background-color: transparent;
	}
	tr {
		display: grid;
		grid-template-columns: 0.7fr repeat(3, minmax(0px, 1fr));
		@include breakpoint(medium) {
			column-gap: $space-sm;
			grid-template-columns: 0.3fr repeat(3, minmax(0px, 1fr));
		}
	}
	th,
	td {
		background-color: $color-white;
		font-weight: normal;
		display: flex;
		align-items: center;
	}
	th.highlight,
	th.Table-cell--highlight {
		font-weight: bold;
	}
	td {
		text-align: right;
		@include text(3);
		@include breakpoint(medium) {
			font-weight: bold;
		}
		@include breakpoint(large) {
			@include text(7);
		}
		> * {
			flex-basis: 100%;
		}
	}
	td.highlight,
	td.Table-cell--highlight {
		background-color: $color-highlight !important;
		font-weight: bold;
	}
	th:first-child {
		background-color: transparent;
		padding-left: 0;
		padding-right: 0;
	}
	th {
		flex-direction: column;
		.Icon {
			@include icon-sm;
		}
		@include breakpoint(medium) {
			flex-direction: row;
			.Icon {
				@include icon-lg;
				@include inline-sm;
			}
		}
	}
}

.Table--borderlessHeaders {
	th {
		border: 0;
	}
}

.Table--mutedHeaders {
	th {
		font-weight: normal;
		text-align: left;
		[dir='rtl'] & {
			text-align: right;
		}
		&.Table-cell--right {
			text-align: right;
			[dir='rtl'] & {
				text-align: left;
			}
		}
	}
}

// used for rowLimit in TableSortable
// makes rows visually hidden only on mobile
.Table-row--sm-visuallyHidden {
	@include breakpoint(medium, down) {
		@include visuallyHidden;
	}
}
</style>
